import { useState, useEffect, useCallback } from 'react'
import { Trophy, Filter, ArrowUpRight, ArrowDownRight } from 'lucide-react'
import api from '../api/client'

const inp = 'rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-accent-500'
const MESES = ['', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

export default function ExtratoPontos() {
  const [dados, setDados] = useState(null)
  const [busy, setBusy] = useState(false)
  const [f, setF] = useState({ mes: '', ano: '', produto: '', cliente: '', evento: '', status: '' })

  const carregar = useCallback(async () => {
    setBusy(true)
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== '' && v != null))
      const { data } = await api.get('/financeiro/pontos', { params })
      setDados(data)
    } catch { setDados({ itens: [], totais: {} }) } finally { setBusy(false) }
  }, [f])
  useEffect(() => { carregar() }, []) // eslint-disable-line

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }))
  const t = dados?.totais || {}

  return (
    <div className="max-w-6xl mx-auto space-y-5">
      <div className="flex items-center gap-2">
        <Trophy className="text-accent-500" size={22} />
        <div>
          <h1 className="text-xl font-heading font-extrabold text-graphite">Extrato de Pontos</h1>
          <p className="text-sm text-gray-500">Pontos (PV) por evento — produção direta e indireta da sua rede.</p>
        </div>
      </div>

      {/* Totais */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[['Pontos Diretos', t.pontos_diretos ?? 0, 'text-emerald-600', ArrowUpRight],
          ['Pontos Indiretos', t.pontos_indiretos ?? 0, 'text-indigo-600', ArrowDownRight],
          ['Total de Pontos', t.total ?? 0, 'text-accent-600', Trophy],
          ['Eventos', t.eventos ?? 0, 'text-neutral-600', Filter]].map(([l, v, cor, Ic]) => (
          <div key={l} className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-4">
            <div className="flex items-center gap-1.5 text-[11px] text-neutral-500"><Ic size={13} /> {l}</div>
            <p className={`text-2xl font-heading font-extrabold mt-1 ${cor}`}>{Number(v).toLocaleString('pt-BR')}</p>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-4">
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
          <select className={inp} value={f.mes} onChange={set('mes')}><option value="">Mês</option>{MESES.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}</select>
          <input className={inp} placeholder="Ano" value={f.ano} onChange={set('ano')} />
          <input className={inp} placeholder="Produto" value={f.produto} onChange={set('produto')} />
          <input className={inp} placeholder="Cliente" value={f.cliente} onChange={set('cliente')} />
          <input className={inp} placeholder="Evento" value={f.evento} onChange={set('evento')} />
          <input className={inp} placeholder="Status" value={f.status} onChange={set('status')} />
        </div>
        <button onClick={carregar} className="mt-3 inline-flex items-center gap-1.5 rounded-xl text-white font-bold px-4 py-2 text-sm" style={{ background: 'linear-gradient(135deg,#ffcc00,#ff6c00)' }}>
          <Filter size={15} /> {busy ? 'Filtrando...' : 'Aplicar filtros'}
        </button>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-500 text-[11px] uppercase tracking-wide">
              <tr>
                {['Data', 'Cliente', 'Produto/Serviço', 'Evento', 'Nível', 'Origem', 'Diretos', 'Indiretos', 'Status'].map((h) => (
                  <th key={h} className="text-left font-semibold px-3 py-2 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {(dados?.itens || []).length === 0 && (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-neutral-400">Nenhum ponto no período.</td></tr>
              )}
              {(dados?.itens || []).map((r) => (
                <tr key={r.id + '-' + r.nivel} className="hover:bg-neutral-50">
                  <td className="px-3 py-2 whitespace-nowrap text-neutral-600">{r.data ? new Date(r.data).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                  <td className="px-3 py-2">{r.cliente || '—'}</td>
                  <td className="px-3 py-2">{r.produto}{r.servico ? <span className="text-neutral-400"> · {r.servico}</span> : ''}</td>
                  <td className="px-3 py-2">{r.evento}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${r.nivel === 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-indigo-50 text-indigo-600'}`}>{r.nivel_label}</span>
                  </td>
                  <td className="px-3 py-2 text-neutral-600">{r.licenciado_origem || '—'}</td>
                  <td className="px-3 py-2 font-bold text-emerald-600">{r.pontos_diretos || ''}</td>
                  <td className="px-3 py-2 font-bold text-indigo-600">{r.pontos_indiretos || ''}</td>
                  <td className="px-3 py-2 text-neutral-500">{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
